export class HowToPanel {
    constructor() {
        this._el = document.createElement('div');
        this._el.className = 'rm-config-panel';
        this._render();
    }

    get element() { return this._el; }

    _render() {
        this._el.innerHTML = `
            <div class="rm-config-section">
                <h3>Welcome to Raven Resume</h3>
                <p style="color:var(--text-muted);line-height:1.6;margin-bottom:16px;">
                    Raven is an AI-powered resume screening and management tool. It uses Claude AI to extract information
                    from resumes, score candidates against your custom evaluation criteria, and manage your hiring pipeline.
                </p>
            </div>

            <div class="rm-config-section">
                <h3>Getting Started with an Existing Campaign</h3>
                <ol style="color:var(--text);line-height:1.8;padding-left:20px;">
                    <li><strong>Go to the Project tab</strong> and select an existing hiring campaign from the project selector (folder icon in the toolbar).</li>
                    <li><strong>Upload resumes</strong> by dragging PDF files into the upload area on the Project tab.</li>
                    <li><strong>Extract information</strong> — click the Extract button in the toolbar. Raven will use AI to pull out names, emails, education, skills, and career summaries.</li>
                    <li><strong>Review candidates</strong> on the Candidates tab. You can sort by any column, set dispositions, and add notes.</li>
                    <li><strong>Run analysis</strong> — click the Analyze button to score all candidates against your selected evaluation criteria.</li>
                    <li><strong>Send emails</strong> — use the Emails tab to create and send templated communications to candidates.</li>
                </ol>
            </div>

            <div class="rm-config-section">
                <h3>Creating a New Hiring Campaign</h3>
                <ol style="color:var(--text);line-height:1.8;padding-left:20px;">
                    <li>Click the <strong>folder icon</strong> in the toolbar and select <strong>New Project</strong>.</li>
                    <li>Give it a descriptive name (e.g., "Pest Control Supervisor - Summer 2026").</li>
                    <li>On the Project tab, configure <strong>CC emails</strong> — anyone who should receive copies of all candidate communications.</li>
                    <li>Add <strong>calendar invitees</strong> — people who should be invited to interview calendar events.</li>
                    <li>Go to the <strong>Criteria tab</strong> to select which evaluation criteria apply to this campaign.</li>
                </ol>
            </div>

            <div class="rm-config-section">
                <h3>Creating Screening Criteria</h3>
                <ol style="color:var(--text);line-height:1.8;padding-left:20px;">
                    <li>Go to the <strong>Criteria tab</strong> and click <strong>+ New Criterion</strong>.</li>
                    <li>Write a clear evaluation prompt that tells the AI what to look for in a resume.</li>
                    <li>Customize the <strong>scale descriptions</strong> for each score range (1-2, 3-4, etc.).</li>
                    <li>Use <strong>Save + Add to Project</strong> to immediately activate this criterion for the current campaign.</li>
                </ol>

                <div style="margin-top:12px;padding:12px;background:var(--bg-secondary);border-radius:8px;border-left:3px solid var(--primary);">
                    <strong style="color:var(--text);">Tips for effective screening prompts:</strong>
                    <ul style="color:var(--text-muted);line-height:1.8;padding-left:20px;margin-top:8px;">
                        <li><strong>Be specific</strong> about what you're evaluating: "Rate this candidate's pest control industry experience" is better than "Rate this candidate."</li>
                        <li><strong>Include context</strong>: "We are hiring for a supervisor role that requires managing a team of 5-10 technicians."</li>
                        <li><strong>Define what good looks like</strong>: Update the scale descriptions to reflect role-specific expectations.</li>
                        <li><strong>Iterate</strong>: After running analysis, review the scores and reasoning. If the AI is scoring too high or low, adjust your prompt and re-run.</li>
                    </ul>
                </div>

                <div style="margin-top:12px;padding:12px;background:var(--bg-secondary);border-radius:8px;border-left:3px solid var(--error);">
                    <strong style="color:var(--text);">Common mistakes:</strong>
                    <ul style="color:var(--text-muted);line-height:1.8;padding-left:20px;margin-top:8px;">
                        <li><strong>Too vague</strong>: "Is this a good candidate?" — gives inconsistent results.</li>
                        <li><strong>No scale context</strong>: Leaving default scale descriptions means the AI doesn't know your standards.</li>
                        <li><strong>Multiple criteria in one prompt</strong>: Better to have separate criteria for "Technical Skills", "Leadership Experience", "Industry Knowledge" than one mega-prompt.</li>
                    </ul>
                </div>
            </div>

            <div class="rm-config-section">
                <h3>Email Communications</h3>
                <ol style="color:var(--text);line-height:1.8;padding-left:20px;">
                    <li>Create email templates on the <strong>Emails tab</strong>.</li>
                    <li>Use <strong>placeholders</strong> like {{firstName}}, {{interviewDate}} for personalization.</li>
                    <li>Set a <strong>target disposition</strong> — only candidates with that status will receive the email.</li>
                    <li>Click <strong>Save + Send Emails</strong> to queue emails for delivery via SendGrid.</li>
                    <li>Track delivery status, opens, and responses in the email log.</li>
                </ol>
            </div>
        `;
    }
}
